import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, MessageCircle, Mail } from "lucide-react";
import { useApi, errMsg, fmtDate } from "@/lib/api";

export function useUnreadChats() {
  const api = useApi();
  const { data = [] } = useQuery({ queryKey: ["admin-chats"], queryFn: () => api("get", "/admin/chats"), refetchInterval: 5000, staleTime: 0 });
  return data.reduce((n, c) => n + (c.unread_admin || 0), 0);
}

export const Bubble = ({ m, mine }) => (
  <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] px-3.5 py-2 rounded-lg text-sm whitespace-pre-wrap break-words ${mine ? "bg-[#0A192F] text-white rounded-br-sm" : "bg-slate-100 text-[#0F172A] rounded-bl-sm"}`}>
      {m.text}
      <p className={`text-[10px] mt-1 ${mine ? "text-slate-400" : "text-slate-500"}`}>{fmtDate(m.created_at)}</p>
    </div>
  </div>
);

function Thread({ cid }) {
  const api = useApi();
  const [text, setText] = useState("");
  const end = useRef(null);
  const q = useQuery({ queryKey: ["admin-chat", cid], queryFn: () => api("get", `/admin/chats/${cid}`), refetchInterval: 3000, staleTime: 0 });
  const msgs = q.data?.messages || [];
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const r = await api("post", `/admin/chats/${cid}/messages`, { text });
      setText("");
      q.refetch();
      if (r.email === "queued") toast.success("Visitor is away, so your reply was also emailed to them");
    }
    catch (err) { toast.error(errMsg(err)); }
  };
  const chat = q.data?.chat;
  return (
    <div className="flex flex-col h-[520px]" data-testid="admin-chat-thread">
      <div className="px-4 py-3 border-b border-slate-200">
        <div className="flex items-center justify-between gap-2">
          <p className="font-display font-semibold text-[#0A192F]" data-testid="admin-chat-name">{chat?.name}</p>
          {chat && <span className={`text-xs font-medium flex items-center gap-1.5 ${chat.visitor_away ? "text-slate-500" : "text-emerald-600"}`} data-testid="admin-chat-presence">
            <span className={`h-2 w-2 rounded-full ${chat.visitor_away ? "bg-slate-300" : "bg-emerald-500"}`} />
            {chat.visitor_away ? (chat.email ? "Away · replies are emailed" : "Away · no email left") : "On the site now"}
          </span>}
        </div>
        {chat?.email && <a href={`mailto:${chat.email}`} className="text-xs text-[#FF6B00] flex items-center gap-1"><Mail className="h-3 w-3" />{chat.email}</a>}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">{msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender === "admin"} />)}<div ref={end} /></div>
      <form onSubmit={send} className="p-3 border-t border-slate-200 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Type a reply..." className="flex-1 h-10 px-3 border border-slate-200 rounded-sm text-sm outline-none focus:border-[#FF6B00]" data-testid="admin-chat-input" />
        <button type="submit" className="h-10 px-4 bg-[#FF6B00] hover:bg-[#E05D00] text-white rounded-sm" data-testid="admin-chat-send"><Send className="h-4 w-4" /></button>
      </form>
    </div>
  );
}

export function ChatsTab() {
  const api = useApi();
  const { data: chats = [] } = useQuery({ queryKey: ["admin-chats"], queryFn: () => api("get", "/admin/chats"), refetchInterval: 5000, staleTime: 0 });
  const [active, setActive] = useState(null);
  if (!chats.length) return <p className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-500" data-testid="chats-empty">No chats yet. Visitors can start one from the chat bubble on any page.</p>;
  return (
    <div className="grid md:grid-cols-[300px_1fr] bg-white border border-slate-200 rounded-sm overflow-hidden">
      <div className="border-b md:border-b-0 md:border-r border-slate-200 max-h-[520px] overflow-y-auto" data-testid="admin-chat-list">
        {chats.map((c) => (
          <button key={c.id} onClick={() => setActive(c.id)} data-testid={`admin-chat-item-${c.id}`}
            className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${active === c.id ? "bg-orange-50" : "hover:bg-slate-50"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-sm truncate">{c.name}</span>
              {c.unread_admin > 0 && <span className="px-1.5 rounded-full bg-[#FF6B00] text-white text-xs">{c.unread_admin}</span>}
            </div>
            <p className="text-xs text-slate-500 truncate">{c.last_message}</p>
            <p className="text-[10px] font-mono text-slate-400">{fmtDate(c.last_message_at)}</p>
          </button>
        ))}
      </div>
      {active ? <Thread key={active} cid={active} /> : (
        <div className="hidden md:flex items-center justify-center text-slate-400 text-sm gap-2 h-[520px]"><MessageCircle className="h-5 w-5" /> Choose a chat to reply</div>
      )}
    </div>
  );
}
